import axios, {AxiosError, AxiosRequestConfig} from 'axios';

import {refreshToken} from '../internal/user/user-api';

const client = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  withCredentials: true
});

let accessToken: string | null = null;
let tokenType: 'LOGIN' | 'CI' | null = null;

export const setAccessToken = (token: string | null, type: 'LOGIN' | 'CI' | null = null) => {
  accessToken = token;
  tokenType = type;
};

export const getAccessToken = () => accessToken;
export const getTokenType = () => tokenType;

export const clearTokens = () => {
  accessToken = null;
  tokenType = null;
};

if (typeof window !== 'undefined') {
  (window as any).getAccessToken = getAccessToken;
  (window as any).getTokenType = getTokenType;
}

client.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

function getPathnameFromConfig(config: AxiosRequestConfig): string {
  const full = axios.getUri({
    ...config,
    baseURL: config.baseURL ?? client.defaults.baseURL
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(full, origin).pathname;
}

client.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original: any = error.config;
    const resp = error.response;
    if (!resp || !original) throw error;

    const pathname = getPathnameFromConfig(original);

    const isRefreshCall = pathname.endsWith('/users/refresh');
    const isLoginCall = pathname.endsWith('/users/login');

    // 무한루프 방지 (로그인, 리프래시콜에선 갱신 시도하면 안됨)
    if (resp.status === 401 && !isRefreshCall && !isLoginCall && !original._retry) {
      original._retry = true;

      if (tokenType === 'LOGIN') {
        try {
          const refreshResponse = await refreshToken();
          const newToken = refreshResponse.accessToken;

          setAccessToken(newToken, 'LOGIN');

          original.headers.Authorization = `Bearer ${newToken}`;
          return client(original);
        } catch (refreshError) {
          console.log('자동 토큰 갱신 실패');
          clearTokens();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } else if (tokenType === 'CI') {
        clearTokens();
        if (typeof window !== 'undefined') {
          window.location.href = '/identity-verification';
        }
        return Promise.reject(error);
      } else {
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default client;
